"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback } from "react";

import CargoDetailsView from "@features/services/shipping/CargoDetailsView";
import ShippingLandingView from "@features/services/shipping/ShippingLandingView";
import TrackingView from "@features/services/shipping/TrackingView";

interface ShippingPageProps {
  bookingOpen: boolean;
}

const ShippingPage = ({ bookingOpen }: ShippingPageProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const viewType = searchParams.get("viewType");
  const trackingRef = searchParams.get("tracking");

  const navigateTo = useCallback(
    (params: Record<string, string>, replace = false) => {
      const sp = new URLSearchParams(params);
      const url = `${pathname}?${sp.toString()}`;
      if (replace) {
        router.replace(url);
      } else {
        router.push(url);
      }
    },
    [router, pathname],
  );

  const navigateHome = useCallback(() => {
    router.push(pathname);
  }, [router, pathname]);

  const onStartShipping = useCallback(() => {
    navigateTo({ viewType: "add-cargo" });
  }, [navigateTo]);

  const onSearchTracking = useCallback(
    (ref: string) => {
      navigateTo({ viewType: "track-shipment", tracking: ref });
    },
    [navigateTo],
  );

  // Once the form mints a tracking reference, pin it in the URL so a refresh
  // or shared link resumes the same shipment.
  const onTrackingRefGenerated = useCallback(
    (ref: string) => {
      navigateTo({ viewType: "add-cargo", tracking: ref }, true);
    },
    [navigateTo],
  );

  if (viewType === "track-shipment" && trackingRef != null) {
    return <TrackingView trackingRef={trackingRef} onBack={navigateHome} />;
  }

  // Resume links (?tracking=…) keep working even when booking is closed.
  if (viewType === "add-cargo" && (bookingOpen || trackingRef != null)) {
    return (
      <CargoDetailsView
        bookingOpen={bookingOpen}
        onBack={navigateHome}
        onTrackingRefGenerated={onTrackingRefGenerated}
        initialTrackingRef={trackingRef}
      />
    );
  }

  return (
    <ShippingLandingView
      bookingOpen={bookingOpen}
      onStartShipping={onStartShipping}
      onSearchTracking={onSearchTracking}
    />
  );
};

export default ShippingPage;
